import type { RibbonControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/Controls/RibbonControlProps';
import { computedFn } from 'mobx-utils/lib/computedFn';
import { constructKeytip, getKeytipString } from 'owa-command-ribbon';
import { getComposeRibbonId } from 'owa-compose-ribbon-utils/lib/utils/getComposeRibbonId';
import { getComposeViewState } from 'owa-mail-reading-pane-store/lib/utils/getComposeViewState';
import type { ComposeViewState } from 'owa-mail-compose-store';
import type { RibbonControlDefinitionProps } from 'owa-mail-ribbon-utils';
import { getControlInfo } from 'owa-mail-ribbon-utils';
import { ActionControlId, MailRibbonControlId, MailRibbonTabId } from 'owa-ribbon-ids';
import { onSaveDraft } from '../../../util/commandActions';
import {
    createMailAppButton,
    createMailAppFlyoutAnchor,
} from '../../../util/createMailRibbonButton';
import { getProtectEncryptSections, getSensitivitySections } from '../../sections';
import { getDefaultRibbonStyles } from 'owa-command-ribbon-styles/lib/util/getDefaultRibbonStyles';
import { getSensitivityMenu } from 'owa-compose-actioncontrols/lib/components/controls/sensitivityMenu';
import Strings from 'owa-locstrings/lib/strings/ribbon_mail_buttons_and_tabs.locstring.json';
import loc from 'owa-localize';

export const getSensitivityControl = computedFn(
    (props: RibbonControlDefinitionProps): RibbonControlProps => {
        const keyTips = constructKeytip(
            [getKeytipString(MailRibbonTabId.Tab_Options)],
            getKeytipString(ActionControlId.Sensitivity),
            true /* hasMenu */
        );
        const menu = { sections: getSensitivitySections(props) };
        return getSensitivityMenu(
            props.popoutComposeViewState ?? getComposeViewState(),
            menu,
            keyTips,
            getDefaultRibbonStyles()
        );
    }
);

export const getEncryptControl = computedFn(
    (
        popoutComposeViewState: ComposeViewState | undefined,
        isPopout: boolean,
        targetWindow: Window
    ): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.Encrypt);
        return createMailAppFlyoutAnchor(
            getComposeRibbonId(MailRibbonControlId.Encrypt, popoutComposeViewState),
            controlInfo.text,
            controlInfo.tooltip,
            isPopout,
            {
                sections: [
                    {
                        title: loc(Strings.encrypt_FlyoutTitle),
                        controls: getProtectEncryptSections(
                            popoutComposeViewState ?? getComposeViewState()!,
                            targetWindow
                        ),
                    },
                ],
            },
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Options)],
                getKeytipString(MailRibbonControlId.Encrypt),
                true /* hasMenu */
            ),
            controlInfo.iconName
        );
    }
);

export const getSaveControl = computedFn(
    (popoutComposeViewState: ComposeViewState | undefined): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.Save);
        return createMailAppButton(
            getComposeRibbonId(MailRibbonControlId.Save, popoutComposeViewState),
            controlInfo.text,
            controlInfo.tooltip,
            popoutComposeViewState != undefined /*isPopout*/,
            () => {
                onSaveDraft(popoutComposeViewState ?? getComposeViewState()!);
            },
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Options)],
                getKeytipString(MailRibbonControlId.Save)
            ),
            controlInfo.iconName
        );
    }
);
